import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  TextField,
  InputAdornment,
  Paper,
  Chip,
  Stack,
} from '@mui/material';
import { Search, Clear } from '@mui/icons-material';
import CircularProgress from '@mui/material/CircularProgress';

const ProgramSearch = ({ 
  searchTerm, 
  onSearchChange, 
  onClearSearch,
  popularSearches = ['MBA', '计算机科学', '医学', '工程', '心理学'],
  resultCount, 
  loading = false
}) => {
  // 内部搜索词状态，用于实时显示输入
  const [internalSearchTerm, setInternalSearchTerm] = useState(searchTerm || '');
  const debounceTimerRef = useRef(null);
  const DEBOUNCE_DELAY = 600; // 防抖延迟时间（毫秒）

  // 同步外部 searchTerm 到内部状态（当外部清空或设置时，比如通过筛选器清空）
  useEffect(() => {
    // 只有当不在加载中且值确实不同时才同步
    // 这避免了在用户输入时被外部值覆盖
    if (!loading && searchTerm !== internalSearchTerm) {
      setInternalSearchTerm(searchTerm || '');
    }
  }, [searchTerm]);

  // 防抖处理搜索
  useEffect(() => {
    // 清除之前的定时器
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // 如果搜索词没有变化，不触发（但允许空值触发，以处理清空操作）
    if (internalSearchTerm === searchTerm && internalSearchTerm !== '') {
      return;
    }

    // 设置新的防抖定时器
    debounceTimerRef.current = setTimeout(() => {
      // 只有在加载完成后才触发搜索
      if (!loading) {
        onSearchChange(internalSearchTerm);
      }
    }, DEBOUNCE_DELAY);

    // 清理函数
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [internalSearchTerm, loading]);

  // 组件卸载时清理定时器
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  const handleSearchChange = (event) => {
    const value = event.target.value;
    // 立即更新内部状态（用于实时显示）
    setInternalSearchTerm(value);
  };

  const handlePopularSearch = (term) => {
    // 直接设置内部状态，防抖会自动触发
    setInternalSearchTerm(term);
  };

  const handleClearSearch = () => {
    setInternalSearchTerm('');
    onSearchChange('');
    if (onClearSearch) onClearSearch();
  };

  return (
    <Paper 
      elevation={2} 
      sx={{ 
        p: 3, 
        mb: 3,
        background: 'linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)',
        borderRadius: 2
      }}
    >
      <Box sx={{ mb: 2 }}>
        <TextField
          fullWidth
          placeholder="搜索专业名称、或学校名称、院系名称、校区."
          value={internalSearchTerm}
          onChange={handleSearchChange}
          disabled={loading}
          variant="outlined"
          sx={{
            '& .MuiOutlinedInput-root': {
              backgroundColor: 'white',
              borderRadius: 2,
              '&:hover': {
                backgroundColor: 'white',
              },
              '&.Mui-focused': {
                backgroundColor: 'white',
              },
              '&.Mui-disabled': {
                backgroundColor: 'rgba(0, 0, 0, 0.05)',
                cursor: 'wait',
              }
            }
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search color={loading ? 'disabled' : 'action'} />
              </InputAdornment>
            ),
            endAdornment: (
              <>
                {loading && internalSearchTerm ? (
                  <InputAdornment position="end">
                    <CircularProgress size={20} sx={{ mr: 1 }} />
                  </InputAdornment>
                ) : null}
                {internalSearchTerm && !loading && (
                  <InputAdornment position="end">
                    <Clear 
                      sx={{ cursor: 'pointer', color: 'action.active' }}
                      onClick={handleClearSearch}
                    />
                  </InputAdornment>
                )}
              </>
            ),
          }}
        />
      </Box>

      {/* 搜索结果数量 */}
      {resultCount !== undefined && (
        <Box sx={{ mb: 2 }}>
          <Box component="span" sx={{ color: 'text.secondary', fontSize: '0.9rem' }}>
            找到 <strong>{resultCount}</strong> 个专业
            {internalSearchTerm && (
              <>
                {' '}关于 "<strong>{internalSearchTerm}</strong>"
              </>
            )}
          </Box>
        </Box>
      )}

      {/* 热门搜索 */}
      {!internalSearchTerm && (
        <Box>
          <Box sx={{ mb: 1, color: 'text.secondary', fontSize: '0.9rem' }}>
            热门搜索：
          </Box>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {popularSearches.map((term, index) => (
              <Chip
                key={index}
                label={term}
                variant="outlined"
                size="small"
                clickable
                disabled={loading}
                onClick={() => handlePopularSearch(term)}
                sx={{
                  '&:hover': {
                    backgroundColor: loading ? 'transparent' : 'primary.main',
                    color: loading ? 'inherit' : 'white',
                    borderColor: loading ? 'inherit' : 'primary.main',
                  }
                }}
              />
            ))}
          </Stack>
        </Box>
      )}
    </Paper>
  );
};

export default ProgramSearch;
